/**
 * Екран платформи без входу (відповідь 401): на вхід з поверненням на цю саму адресу (UX-06).
 * Перехід замінює поточний запис історії: «Назад» зі сторінки входу не повертає на екран, що
 * одразу знову відправив би на вхід.
 *
 * Адреса — екрана, який показано (`resolvedLocation`): поки документ входу вантажиться, старий
 * екран лишається, і `location` — це вже `/login`. Виняток — перехід у дорозі на інший екран
 * (`loader` маршруту, блок Q): 401 його даних отримує ще старий екран, але людина йде туди, тож
 * `next` — адреса переходу (`location`), якщо це не сам вхід. Тому ж перехід — один раз на екран.
 */
import { type RouterState, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { LOGIN_PATH } from '../app/next-path.ts';
import { resetPersonPrefs } from '../prefs/prefs.ts';
import { isLeaving } from './sign-out.ts';

/** Куди повернутись після входу: показаний екран, а під час переходу (не на вхід) — його ціль. */
export function signInNext(
  s: Pick<RouterState, 'status' | 'location' | 'resolvedLocation'>,
): string {
  if (s.status === 'pending' && s.location.pathname !== LOGIN_PATH) return s.location.href;
  return (s.resolvedLocation ?? s.location).href;
}

export function useSignInRedirect(unauthorized: boolean): void {
  const navigate = useNavigate();
  const here = useRouterState({ select: signInNext });
  const sent = useRef(false);
  useEffect(() => {
    // «Вийти» вже веде на `/login` без `next`: 401 від перезапиту після очищення кешу не втручається.
    if (!unauthorized || sent.current || isLeaving()) return;
    sent.current = true;
    // Сесія спливла чи відкликана без «Вийти»: наступна людина на цьому пристрої не успадкує поля
    // попередньої (пояс, стартову…); тема й щільність пристрою лишаються.
    resetPersonPrefs();
    void navigate({ to: '/login', search: { next: here }, replace: true });
  }, [unauthorized, here, navigate]);
}
